import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import ContactMap from './ContactMap';
import ObjectivesTracker from './ObjectivesTracker';
import { WFD_OBJECTIVES, detectObjectives, calculateObjectiveMultiplier } from '../data/wfdObjectives';
import CustomSelect from './CustomSelect';
import { detectArfdObjectives, calculateArfdScore } from '../data/arfdObjectives';
import { getSectionName } from '../data/sections';
import { getDxCountry } from '../data/dxccPrefixes';

// Modes available for both contests. FT8/FT4 added for ARRD digital ops; SAT for ARFD satellite QSOs.
const MODES = ['SSB', 'CW', 'FM', 'AM', 'FT8', 'FT4', 'RTTY', 'PSK31', 'OLIVIA', 'JS8', 'PACKET', 'SSTV', 'SAT'];

const WFD_BANDS = [
  { label: '160m', freq: 1.900, min: 1.8, max: 2.0 },
  { label: '80m', freq: 3.900, min: 3.5, max: 4.0 },
  { label: '40m', freq: 7.200, min: 7.0, max: 7.3 },
  { label: '20m', freq: 14.250, min: 14.0, max: 14.35 },
  { label: '15m', freq: 21.300, min: 21.0, max: 21.45 },
  { label: '10m', freq: 28.400, min: 28.0, max: 29.7 },
  { label: '6m', freq: 50.150, min: 50.0, max: 54.0 },
  { label: '2m', freq: 146.520, min: 144.0, max: 148.0 },
  { label: '70cm', freq: 446.000, min: 420.0, max: 450.0 },
];

// ARFD adds WARC bands (30m/17m/12m) and a SAT pseudo-band (freq=0)
const ARFD_BANDS = [
  { label: '160m', freq: 1.900, min: 1.8, max: 2.0 },
  { label: '80m', freq: 3.900, min: 3.5, max: 4.0 },
  { label: '40m', freq: 7.200, min: 7.0, max: 7.3 },
  { label: '30m', freq: 10.130, min: 10.1, max: 10.15 },
  { label: '20m', freq: 14.250, min: 14.0, max: 14.35 },
  { label: '17m', freq: 18.100, min: 18.068, max: 18.168 },
  { label: '15m', freq: 21.300, min: 21.0, max: 21.45 },
  { label: '12m', freq: 24.940, min: 24.89, max: 24.99 },
  { label: '10m', freq: 28.400, min: 28.0, max: 29.7 },
  { label: '6m', freq: 50.150, min: 50.0, max: 54.0 },
  { label: '2m', freq: 146.520, min: 144.0, max: 148.0 },
  { label: '70cm', freq: 446.000, min: 420.0, max: 450.0 },
  { label: 'SAT', freq: 0, satellite: true },
];

function gridToLatLon(grid) {
  if (!grid || grid.length < 4) return null;
  grid = grid.toUpperCase();
  
  const lon = (grid.charCodeAt(0) - 65) * 20 - 180;
  const lat = (grid.charCodeAt(1) - 65) * 10 - 90;
  const lonSub = parseInt(grid[2]) * 2;
  const latSub = parseInt(grid[3]);
  
  let finalLon = lon + lonSub + 1;
  let finalLat = lat + latSub + 0.5;
  
  if (grid.length >= 6) {
    const lonSubSub = (grid.charCodeAt(4) - 65) * (2 / 24);
    const latSubSub = (grid.charCodeAt(5) - 65) * (1 / 24);
    finalLon = lon + lonSub + lonSubSub + (1 / 24);
    finalLat = lat + latSub + latSubSub + (0.5 / 24);
  }
  
  return { lat: finalLat, lon: finalLon };
}

function getBandFromFreq(freq, bands) {
  const f = parseFloat(freq);
  if (!f || f === 0) return 'SAT';
  for (const band of bands) {
    if (band.satellite) continue;
    if (f >= band.min && f <= band.max) return band.label;
  }
  return 'Other';
}

function BarChart({ data, title, color = 'var(--accent-primary)' }) {
  if (!data || data.length === 0) return null;
  const maxValue = Math.max(...data.map(d => d.count));
  
  return (
    <div className="chart-container">
      <h3 className="chart-title">{title}</h3>
      <div className="chart-bars">
        {data.slice(0, 10).map((item, i) => (
          <div key={item.label} className="chart-row">
            <span className="chart-label">{item.label}</span>
            <div className="chart-bar-bg">
              <div 
                className="chart-bar-fill"
                style={{ 
                  width: `${(item.count / maxValue) * 100}%`,
                  background: `linear-gradient(90deg, ${color}, ${color}88)`
                }}
              />
            </div>
            <span className="chart-value">{item.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Dashboard({ token, user, socket, apiUrl, showObjectives, onCloseObjectives, eventType = 'WFD', onEventTypeChange }) {
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({ total_contacts: 0, unique_callsigns: 0, unique_sections: 0, modes_used: 0 });
  const [, setSectionCounts] = useState({});
  const [newContactId, setNewContactId] = useState(null);
  const [objectives, setObjectives] = useState({});
  const [settings, setSettings] = useState({ club_grid: '', club_section: '', event_class: 'O', event_category: '1', event_type: 'WFD' });

  const isArfd = eventType === 'ARFD';
  const BANDS = isArfd ? ARFD_BANDS : WFD_BANDS;

  // Only show contacts for the active contest on the dashboard
  const activeContacts = useMemo(() => contacts.filter(c => {
    const ct = c.contest_type || (/^\d+[ABCDEF]$/.test(c.class) ? 'ARFD' : 'WFD');
    return ct === eventType;
  }), [contacts, eventType]);
  
  const chartData = useMemo(() => {
    if (!activeContacts.length) return { bands: [], sections: [], classes: [], operators: [], modes: [] };
    
    const bandCounts = {};
    const sectionCountsLocal = {};
    const classCounts = {};
    const opCounts = {};
    const modeCounts = {};
    
    activeContacts.forEach(c => {
      const band = getBandFromFreq(c.frequency, BANDS);
      bandCounts[band] = (bandCounts[band] || 0) + 1;
      sectionCountsLocal[c.section] = (sectionCountsLocal[c.section] || 0) + 1;
      classCounts[c.class] = (classCounts[c.class] || 0) + 1;
      opCounts[c.operator_callsign] = (opCounts[c.operator_callsign] || 0) + 1;
      modeCounts[c.mode] = (modeCounts[c.mode] || 0) + 1;
    });
    
    const toSorted = (obj) => Object.entries(obj)
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count);
    
    return {
      bands: toSorted(bandCounts),
      sections: toSorted(sectionCountsLocal),
      classes: toSorted(classCounts),
      operators: toSorted(opCounts),
      modes: toSorted(modeCounts)
    };
  }, [activeContacts, BANDS]);
  
  const contactMarkers = useMemo(() => {
    return activeContacts
      .filter(c => c.contact_lat && c.contact_lon)
      .map(c => ({
        callsign: c.contact_callsign,
        lat: parseFloat(c.contact_lat),
        lon: parseFloat(c.contact_lon),
        grid: c.contact_grid,
        city: c.contact_city,
        state: c.contact_state
      }));
  }, [activeContacts]);

  const autoDetected = useMemo(() => {
    return isArfd ? detectArfdObjectives(activeContacts) : detectObjectives(activeContacts);
  }, [activeContacts, isArfd]);

  const scoreData = useMemo(() => {
    if (isArfd) {
      const completed = {};
      objectives && Object.keys(objectives).forEach(id => {
        completed[id] = objectives[id]?.completed || autoDetected[id];
      });
      const youthCount = parseInt(objectives['youth_operators']?.notes, 10) || 0;
      const arfd = calculateArfdScore(activeContacts, completed, youthCount);
      return {
        qsoPoints: arfd.rawQsoPoints,
        // For the stat cards: show power multiplier and bonus points separately
        powerMultiplier: arfd.powerMultiplier,
        bonusPoints: arfd.bonusPoints,
        multiplier: null, // not used in ARFD display
        totalScore: arfd.finalScore
      };
    }

    // WFD
    const completed = {};
    WFD_OBJECTIVES.forEach(obj => {
      completed[obj.id] = objectives[obj.id]?.completed || autoDetected[obj.id];
    });
    const multiplier = calculateObjectiveMultiplier(completed);
    const qsoPoints = activeContacts.reduce((sum, c) => {
      const mode = c.mode?.toUpperCase();
      if (mode === 'CW' || ['RTTY', 'PSK31', 'OLIVIA', 'JS8', 'FT8', 'FT4'].includes(mode)) {
        return sum + 2;
      }
      return sum + 1;
    }, 0);
    return {
      qsoPoints,
      multiplier: multiplier + 1,
      powerMultiplier: null,
      bonusPoints: null,
      totalScore: qsoPoints * (multiplier + 1)
    };
  }, [activeContacts, objectives, autoDetected, isArfd]);
  
  const [form, setForm] = useState({
    contactCallsign: '',
    frequency: localStorage.getItem('aurora_frequency') || '',
    mode: localStorage.getItem('aurora_mode') || 'SSB',
    class: '',
    section: '',
    gridsquare: ''
  });
  const [editingContact, setEditingContact] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [lookupInfo, setLookupInfo] = useState(null);
  const [sectionLookupName, setSectionLookupName] = useState(null);
  const [missingFields, setMissingFields] = useState([]);

  const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

  // Warn if the callsign in the form has already been worked on the same band within 24h
  const duplicateWarning = useMemo(() => {
    const callsign = form.contactCallsign?.trim().toUpperCase();
    if (!callsign || callsign.length < 3 || !form.frequency) return null;
    const formBand = getBandFromFreq(parseFloat(form.frequency), BANDS);
    if (formBand === 'Other') return null;
    const now = Date.now();
    const prior = activeContacts.find(c => {
      if (editingContact && c.id === editingContact.id) return false;
      if (c.contact_callsign?.trim().toUpperCase() !== callsign) return false;
      if (getBandFromFreq(parseFloat(c.frequency), BANDS) !== formBand) return false;
      return Math.abs(now - new Date(c.timestamp).getTime()) < TWENTY_FOUR_HOURS_MS;
    });
    if (!prior) return null;
    const d = new Date(prior.timestamp);
    const timeStr = d.toISOString().replace('T', ' ').substring(0, 16) + 'Z';
    return `Already worked ${callsign} on ${formBand} at ${timeStr}`;
  }, [form.contactCallsign, form.frequency, activeContacts, editingContact, BANDS, TWENTY_FOUR_HOURS_MS]);
  
  const callsignInputRef = useRef(null);
  const lookupTimeoutRef = useRef(null);

  useEffect(() => {
    if (form.frequency) localStorage.setItem('aurora_frequency', form.frequency);
    if (form.mode) localStorage.setItem('aurora_mode', form.mode);
  }, [form.frequency, form.mode]);

  const fetchContacts = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(response.data);
    } catch (err) {
      console.error('Error fetching contacts:', err);
    }
  }, [apiUrl, token]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, [apiUrl, token]);

  const fetchSectionCounts = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/sections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSectionCounts(response.data);
    } catch (err) {
      console.error('Error fetching section counts:', err);
    }
  }, [apiUrl, token]);

  const fetchObjectives = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/objectives`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setObjectives(response.data);
    } catch (err) {
      console.error('Error fetching objectives:', err);
    }
  }, [apiUrl, token]);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
      // Keep App.js eventType in sync when settings load
      if (response.data.event_type && onEventTypeChange) {
        onEventTypeChange(response.data.event_type);
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  }, [apiUrl, token, onEventTypeChange]);

  useEffect(() => {
    fetchContacts();
    fetchStats();
    fetchSectionCounts();
    fetchObjectives();
    fetchSettings();
  }, [fetchContacts, fetchStats, fetchSectionCounts, fetchObjectives, fetchSettings]);

  useEffect(() => {
    if (socket) {
      socket.on('newContact', (contact) => {
        setContacts(prev => [contact, ...prev]);
        setNewContactId(contact.id);
        setTimeout(() => setNewContactId(null), 1000);
        fetchStats();
        fetchSectionCounts();
      });

      socket.on('contactUpdated', (contact) => {
        setContacts(prev => prev.map(c => c.id === contact.id ? contact : c));
        fetchStats();
        fetchSectionCounts();
      });

      socket.on('contactDeleted', ({ id }) => {
        setContacts(prev => prev.filter(c => c.id !== id));
        fetchStats();
        fetchSectionCounts();
      });

      return () => {
        socket.off('newContact');
        socket.off('contactUpdated');
        socket.off('contactDeleted');
      };
    }
  }, [socket, fetchStats, fetchSectionCounts]);

  const lookupCallsign = useCallback(async (callsign) => {
    if (callsign.length < 3) {
      setLookupInfo(null);
      return null;
    }

    try {
      const response = await axios.get(`${apiUrl}/api/callsign/${callsign}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLookupInfo(response.data);
      return response.data;
    } catch (err) {
      setLookupInfo(null);
      return null;
    }
  }, [apiUrl, token]);

  const handleCallsignChange = (value) => {
    const upper = value.toUpperCase();
    setForm(prev => ({ ...prev, contactCallsign: upper }));
    
    if (lookupTimeoutRef.current) {
      clearTimeout(lookupTimeoutRef.current);
    }
    
    lookupTimeoutRef.current = setTimeout(async () => {
      const info = await lookupCallsign(upper);
      if (info?.grid_square) {
        setForm(prev => ({ ...prev, gridsquare: info.grid_square.toUpperCase() }));
      }
    }, 500);
  };

  const handleSectionChange = (value) => {
    const upper = value.toUpperCase();
    setForm(prev => ({ ...prev, section: upper }));
    if (upper.length >= 2) {
      const name = getSectionName(upper);
      // getSectionName returns the code itself when not found
      setSectionLookupName(name !== upper ? name : null);
    } else {
      setSectionLookupName(null);
    }
  };

  const handleBandSelect = (freq) => {
    setForm(prev => ({ ...prev, frequency: freq.toString() }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Client-side required field validation
    const required = ['contactCallsign', 'frequency', 'mode', 'class', 'section'];
    const missing = required.filter(f => !form[f]?.toString().trim());
    if (missing.length > 0) {
      setMissingFields(missing);
      setError('Please fill in all required fields.');
      return;
    }
    setMissingFields([]);
    setSubmitting(true);

    try {
      const submitForm = { ...form, frequency: form.frequency.toString().replace(',', '.') };
      if (editingContact) {
        await axios.put(`${apiUrl}/api/contacts/${editingContact.id}`, submitForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setEditingContact(null);
      } else {
        await axios.post(`${apiUrl}/api/contacts`, submitForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      localStorage.setItem('aurora_frequency', form.frequency);
      localStorage.setItem('aurora_mode', form.mode);
      setForm({
        contactCallsign: '',
        frequency: form.frequency,
        mode: form.mode,
        class: '',
        section: '',
        gridsquare: ''
      });
      setLookupInfo(null);
      setSectionLookupName(null);
      callsignInputRef.current?.focus();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to log contact');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setForm({
      contactCallsign: contact.contact_callsign,
      frequency: contact.frequency,
      mode: contact.mode,
      class: contact.class,
      section: contact.section,
      time: contact.time || '',
      gridsquare: contact.contact_grid || ''
    });
    if (contact.section) {
      const name = getSectionName(contact.section);
      setSectionLookupName(name !== contact.section ? name : null);
    }
    callsignInputRef.current?.focus();
  };

  const handleCancelEdit = () => {
    setEditingContact(null);
    setForm({
      contactCallsign: '',
      frequency: form.frequency,
      mode: form.mode,
      class: '',
      section: '',
      time: '',
      gridsquare: ''
    });
    setSectionLookupName(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    
    try {
      await axios.delete(`${apiUrl}/api/contacts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete contact');
    }
  };

  return (
    <main className="main-content">
      {showObjectives && (
        <div className="modal-overlay" onClick={onCloseObjectives}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={onCloseObjectives}>×</button>
            <ObjectivesTracker 
              token={token} 
              apiUrl={apiUrl} 
              contacts={activeContacts}
              isAdmin={user?.isAdmin}
              onUpdate={fetchObjectives}
              eventType={eventType}
            />
          </div>
        </div>
      )}

      <div className={`stats-grid ${isArfd ? 'stats-grid-7' : 'stats-grid-7'}`}>
        <div className="stat-card">
          <div className="stat-value">{stats.total_contacts}</div>
          <div className="stat-label">Total QSOs</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.unique_callsigns}</div>
          <div className="stat-label">Unique Calls</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.unique_sections}</div>
          <div className="stat-label">Sections</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.modes_used}</div>
          <div className="stat-label">Modes</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{scoreData.qsoPoints}</div>
          <div className="stat-label">QSO Points</div>
        </div>
        {isArfd ? (
          <>
            <div className="stat-card">
              <div className="stat-value">x{scoreData.powerMultiplier}</div>
              <div className="stat-label">Power Mult.</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">+{scoreData.bonusPoints}</div>
              <div className="stat-label">Bonus Pts</div>
            </div>
          </>
        ) : (
          <div className="stat-card">
            <div className="stat-value">x{scoreData.multiplier}</div>
            <div className="stat-label">Multiplier</div>
          </div>
        )}
        <div className="stat-card stat-card-highlight">
          <div className="stat-value">{scoreData.totalScore.toLocaleString()}</div>
          <div className="stat-label">Total Score</div>
        </div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 className="card-title" style={{ margin: 0 }}>
            {editingContact ? `Edit Contact: ${editingContact.contact_callsign}` : 'Log Contact'}
          </h2>
          {editingContact && (
            <button type="button" className="btn btn-secondary btn-small" onClick={handleCancelEdit}>
              Cancel
            </button>
          )}
        </div>
        
        {settings.club_section && (
          <div className="exchange-display">
            <span className="exchange-label">Your Exchange:</span>
            {settings.club_callsign && <span className="exchange-callsign">{settings.club_callsign}</span>}
            <span className="exchange-value">{settings.event_category}{settings.event_class} {settings.club_section}</span>
            {settings.club_grid && <span className="exchange-grid">Grid: {settings.club_grid}</span>}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label>Callsign</label>
              <input
                ref={callsignInputRef}
                type="text"
                value={form.contactCallsign}
                onChange={(e) => { handleCallsignChange(e.target.value); setMissingFields(p => p.filter(f => f !== 'contactCallsign')); }}
                placeholder="W1AW"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="characters"
                style={{ textTransform: 'uppercase' }}
                className={missingFields.includes('contactCallsign') ? 'invalid' : ''}
              />
              {lookupInfo && (
                <small style={{ color: 'var(--accent-aurora)', fontSize: '0.75rem' }}>
                  {lookupInfo.name} • {lookupInfo.grid_square || lookupInfo.state || lookupInfo.country}
                </small>
              )}
              {duplicateWarning && (
                <small style={{ color: 'var(--error)', fontSize: '0.75rem', display: 'block', marginTop: '2px' }}>
                  Warning: {duplicateWarning}
                </small>
              )}
            </div>
            
            <div className="form-group">
              <label>Frequency (MHz)</label>
              <input
                type="text"
                inputMode="decimal"
                value={form.frequency}
                onChange={(e) => { setForm(prev => ({ ...prev, frequency: e.target.value.replace(',', '.') })); setMissingFields(p => p.filter(f => f !== 'frequency')); }}
                placeholder="14.250"
                autoComplete="off"
                className={missingFields.includes('frequency') ? 'invalid' : ''}
              />
            </div>
            
            <div className="form-group">
              <label>Mode</label>
              <CustomSelect
                value={form.mode}
                onChange={(val) => setForm(prev => ({ ...prev, mode: val }))}
                options={MODES}
              />
            </div>
            
            <div className="form-group">
              <label>Class</label>
              <input
                type="text"
                value={form.class}
                onChange={(e) => { setForm(prev => ({ ...prev, class: e.target.value.toUpperCase() })); setMissingFields(p => p.filter(f => f !== 'class')); }}
                placeholder={isArfd ? '2A' : '1O'}
                maxLength="4"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="characters"
                style={{ textTransform: 'uppercase' }}
                className={missingFields.includes('class') ? 'invalid' : ''}
              />
            </div>
            
            <div className="form-group">
              <label>Section</label>
              <input
                type="text"
                value={form.section}
                onChange={(e) => { handleSectionChange(e.target.value); setMissingFields(p => p.filter(f => f !== 'section')); }}
                placeholder="CO"
                maxLength="4"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="characters"
                style={{ textTransform: 'uppercase' }}
                className={missingFields.includes('section') ? 'invalid' : ''}
              />
              {sectionLookupName && (
                <small style={{ color: 'var(--accent-aurora)', fontSize: '0.75rem' }}>
                  {sectionLookupName}
                </small>
              )}
              {!sectionLookupName && form.section === 'DX' && (() => {
                const country = lookupInfo?.country || getDxCountry(form.contactCallsign);
                return country ? (
                  <small style={{ color: 'var(--accent-aurora)', fontSize: '0.75rem' }}>
                    {country}
                  </small>
                ) : null;
              })()}
            </div>
            
            <div className="form-group">
              <label>Grid Square</label>
              <input
                type="text"
                value={form.gridsquare}
                onChange={(e) => setForm(prev => ({ ...prev, gridsquare: e.target.value.toUpperCase() }))}
                placeholder="DM79"
                maxLength="6"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="characters"
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            
          </div>
          
          <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
            <button type="submit" className="btn" disabled={submitting}>
              {submitting ? (editingContact ? 'Saving...' : 'Logging...') : (editingContact ? 'Save Changes' : 'Log QSO')}
            </button>
          </div>
          
          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {BANDS.map(band => (
              <button
                key={band.label}
                type="button"
                className={`btn btn-small ${parseFloat(form.frequency) === band.freq ? '' : 'btn-secondary'}`}
                onClick={() => handleBandSelect(band.freq)}
              >
                {band.label}
              </button>
            ))}
          </div>
          
          {error && <div className="error-message" style={{ marginTop: '1rem' }}>{error}</div>}
        </form>
      </div>

      <div className="card">
        <h2 className="card-title">Contact Map</h2>
        <div className="map-container">
          <ContactMap 
            sectionCounts={Object.fromEntries(chartData.sections.map(s => [s.label, s.count]))}
            contactMarkers={contactMarkers}
            clubQth={settings.club_grid ? {
              ...gridToLatLon(settings.club_grid),
              grid: settings.club_grid,
              callsign: settings.club_callsign
            } : null}
          />
        </div>
      </div>

      <div className="charts-grid">
        <div className="card">
          <BarChart data={chartData.bands} title="Top Bands" color="#38bdf8" />
        </div>
        <div className="card">
          <BarChart data={chartData.sections} title="Top Sections" color="#34d399" />
        </div>
        <div className="card">
          <BarChart data={chartData.modes} title="Top Modes" color="#a78bfa" />
        </div>
        <div className="card">
          <BarChart data={chartData.operators} title="Top Operators" color="#fb923c" />
        </div>
      </div>

      <div className="card">
        <h2 className="card-title">Recent Contacts</h2>
        <div className="contacts-list">
          {activeContacts.map(contact => (
            <div 
              key={contact.id}
              className={`contact-row ${contact.id === newContactId ? 'new-contact-flash' : ''}`}
            >
              <div className="contact-main">
                <span className="contact-callsign">{contact.contact_callsign}</span>
                <span className="contact-exchange">{contact.class} {contact.section}</span>
                {contact.contact_grid && <span className="grid-badge">{contact.contact_grid}</span>}
              </div>
              <div className="contact-details">
                <span className="contact-freq">{parseFloat(contact.frequency).toFixed(3)} MHz</span>
                <span className="contact-mode">{contact.mode}</span>
                <span className="contact-time">{formatTime(contact.timestamp)}</span>
                <span className="contact-op">op: {contact.operator_callsign}</span>
              </div>
              {(contact.contact_city || contact.contact_state) && (
                <div className="contact-qth">
                  {contact.contact_city}{contact.contact_city && contact.contact_state && ', '}{contact.contact_state}
                </div>
              )}
              <div className="contact-actions">
                <button 
                  className="btn-icon" 
                  onClick={() => handleEdit(contact)}
                  title="Edit"
                >
                  ✎
                </button>
                <button 
                  className="btn-icon delete" 
                  onClick={() => handleDelete(contact.id)}
                  title="Delete"
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
          {contacts.length === 0 && (
            <div className="contacts-empty">No contacts logged yet</div>
          )}
        </div>
      </div>
    </main>
  );
}

export default Dashboard;
